/* Driver.js-powered interactive product tour for new founders.
 *
 * Usage:
 *   import { startFounderTour, maybeStartFounderTourOnFirstVisit } from "../lib/tour";
 *   maybeStartFounderTourOnFirstVisit();   // auto-fires once per user
 *   startFounderTour();                    // explicit replay button
 *
 * Selectors target existing `data-testid` attributes — no extra markup needed.
 */
import { driver } from "driver.js";
import "driver.js/dist/driver.css";

const STORAGE_KEY = "clinq-founder-tour-completed-v1";
const FUNDER_STORAGE_KEY = "clinq-funder-tour-completed-v1";

// SECURITY NOTE: Both keys store a single non-sensitive UX flag ("1" once
// the founder/funder has completed the tour). NO PII, NO auth tokens, NO
// session state — the app's auth runs over HTTP-only cookies via
// `withCredentials: true` in `api.js`. localStorage is intentional here so
// the flag survives across sessions on the same device.

/** localStorage can throw in private-browsing / quota-full modes — wrap so the tour never breaks the page. */
function safeLocalStorageGet(key) {
  try { return localStorage.getItem(key); }
  catch (err) {
    if (process.env.NODE_ENV === "development") console.warn("[tour] localStorage.get failed", err);
    return null;
  }
}
function safeLocalStorageSet(key, value) {
  try { localStorage.setItem(key, value); }
  catch (err) {
    if (process.env.NODE_ENV === "development") console.warn("[tour] localStorage.set failed", err);
  }
}
function safeLocalStorageRemove(key) {
  try { localStorage.removeItem(key); }
  catch (err) {
    if (process.env.NODE_ENV === "development") console.warn("[tour] localStorage.remove failed", err);
  }
}

/** Brand-tinted defaults shared by every step. */
const driverConfig = {
  showProgress: true,
  smoothScroll: true,
  allowClose: true,
  overlayOpacity: 0.55,
  stagePadding: 8,
  stageRadius: 12,
  popoverClass: "clinq-tour-popover",
  nextBtnText: "Next →",
  prevBtnText: "← Back",
  doneBtnText: "Got it",
  progressText: "Step {{current}} of {{total}}",
};

const STEPS = [
  {
    popover: {
      title: "Welcome to Clinq 👋",
      description:
        "This 60-second tour walks you through your Funder-Readiness dashboard. " +
        "You can replay it any time from the Tutorial page.",
      side: "over",
      align: "center",
    },
  },
  {
    element: '[data-testid="shell-logo"]',
    popover: {
      title: "Your nav",
      description:
        "Jump between Dashboard, Team, Services, and Onboarding from up here.",
      side: "bottom",
      align: "start",
    },
  },
  {
    element: '[data-testid="score-card"]',
    popover: {
      title: "Your CCR Score™",
      description:
        "A 0–100 score updated whenever you upload a document. The breakdown below shows exactly what's pulling it up or down.",
      side: "right",
      align: "start",
    },
  },
  {
    element: '[data-testid="completion-banner"]',
    popover: {
      title: "Your completion checklist",
      description:
        "Tick every item to unlock funder matches and the AI Outreach feature. We tell you exactly what's missing.",
      side: "right",
      align: "start",
    },
  },
  {
    element: '[data-testid="matches-card"]',
    popover: {
      title: "Funder matches",
      description:
        "Once your checklist is complete, this card shows the funders that best match your sector, ticket, country, and impact areas.",
      side: "left",
      align: "start",
    },
  },
  {
    element: '[data-testid="team-card"]',
    popover: {
      title: "Your team",
      description:
        "Invite your co-founder, CFO, or COO to collaborate on the same business profile.",
      side: "left",
      align: "start",
    },
  },
  {
    popover: {
      title: "Ready when you are",
      description:
        "Next up: head to <b>Onboarding</b> to upload your pitch deck, financial model, and management accounts. " +
        "Need a Clinq strategist? The <b>Services</b> tab has half-price launch offers.",
      side: "over",
      align: "center",
    },
  },
];

/** Filter steps whose anchor element isn't on the current page, so the tour
 *  doesn't 404 if the dashboard layout changes or a card is hidden. */
function filterAvailableSteps() {
  return STEPS.filter((s) => {
    if (!s.element) return true; // intro/outro
    return document.querySelector(s.element);
  });
}

export function startFounderTour() {
  const steps = filterAvailableSteps();
  if (steps.length === 0) return;
  const tour = driver({
    ...driverConfig,
    steps,
    onDestroyed: () => {
      safeLocalStorageSet(STORAGE_KEY, new Date().toISOString());
    },
  });
  tour.drive();
}

export function maybeStartFounderTourOnFirstVisit() {
  if (safeLocalStorageGet(STORAGE_KEY)) return false;
  // Slight delay so cards fully render
  setTimeout(startFounderTour, 700);
  return true;
}

export function resetFounderTour() {
  safeLocalStorageRemove(STORAGE_KEY);
}

/* ============================================================
 *          David's guided platform walkthrough
 * ============================================================
 * Launched from the David welcome popup ("Take me through the platform").
 * Covers every key page/section: dashboard, CRS score & report, action
 * checklist, cohort benchmark, team, document upload and the rest of the nav.
 */
const DAVID_STEPS = [
  {
    popover: {
      title: "Let's take the tour 👋",
      description:
        "I'll walk you through every part of Clinq and why it matters for your capital raise. Takes about a minute.",
      side: "over",
      align: "center",
    },
  },
  {
    element: '[data-testid="score-card"]',
    popover: {
      title: "Your CRS Score & Report",
      description:
        "This is your Capital Readiness Score — the number funders see first. It updates every time you upload a document, and your full report explains exactly what's pulling it up or down.",
      side: "right",
      align: "start",
    },
  },
  {
    element: '[data-testid="completion-banner"]',
    popover: {
      title: "Your action checklist",
      description:
        "Your to-do list, personalised to the type of capital you're raising. Tick everything off to unlock your score, funder matches and outreach.",
      side: "right",
      align: "start",
    },
  },
  {
    element: '[data-testid="benchmark-panel"]',
    popover: {
      title: "Cohort benchmark",
      description:
        "See how your readiness compares with founders like you — same sector, similar stage. Funders think in comparisons; now you can too.",
      side: "top",
      align: "start",
    },
  },
  {
    element: '[data-testid="matches-card"]',
    popover: {
      title: "Funder matches",
      description:
        "Once your profile is complete, this shows the funders that best fit your sector, ticket size, country and capital type.",
      side: "left",
      align: "start",
    },
  },
  {
    element: '[data-testid="team-card"]',
    popover: {
      title: "Your team",
      description:
        "Raising capital is a team sport. Invite your co-founder, CFO or COO here to collaborate on the same profile and documents.",
      side: "left",
      align: "start",
    },
  },
  {
    element: '[data-testid="shell-nav-onboarding"]',
    popover: {
      title: "Document upload",
      description:
        "This is where your documents live — bank statements, financials, and everything funders will ask for. Upload here any time and your score refreshes automatically.",
      side: "bottom",
      align: "start",
    },
  },
  {
    element: '[data-testid="shell-nav-team"]',
    popover: {
      title: "Team page",
      description:
        "Manage invites and see who's working on your raise with you.",
      side: "bottom",
      align: "start",
    },
  },
  {
    element: '[data-testid="shell-nav-tutorial"]',
    popover: {
      title: "Tutorial",
      description:
        "Short guides on getting the most out of Clinq — and you can replay this tour from here whenever you like.",
      side: "bottom",
      align: "start",
    },
  },
  {
    popover: {
      title: "You are all set 🎉",
      description:
        "You are all set. Come back and ask me anything anytime. — David",
      side: "over",
      align: "center",
    },
  },
];

export function startDavidWalkthrough() {
  const steps = DAVID_STEPS.filter((s) => !s.element || document.querySelector(s.element));
  if (steps.length === 0) return;
  const tour = driver({
    ...driverConfig,
    steps,
    doneBtnText: "Thanks, David!",
    onDestroyed: () => {
      safeLocalStorageSet(STORAGE_KEY, new Date().toISOString());
    },
  });
  tour.drive();
}

/* ============================================================
 *                   FUNDER tour
 * ============================================================ */
const FUNDER_STEPS = [
  {
    popover: {
      title: "Welcome to the Clinq Terminal 👋",
      description:
        "A 60-second tour of your Funder Terminal: deals, mandate, and pipeline. " +
        "You can replay it any time from the Tutorial page.",
      side: "over",
      align: "center",
    },
  },
  {
    element: '[data-testid="shell-logo"]',
    popover: {
      title: "Your nav",
      description:
        "Jump between Terminal, Tutorial, Pipeline, Saved, Analytics, and Mandate from up here.",
      side: "bottom",
      align: "start",
    },
  },
  {
    element: '[data-testid="terminal-header"]',
    popover: {
      title: "The Terminal",
      description:
        "Live deal flow filtered to your mandate. Every SME here matches your sector, ticket, country, and impact preferences (≥40% compatibility).",
      side: "bottom",
      align: "center",
    },
  },
  {
    element: '[data-testid="terminal-search"]',
    popover: {
      title: "Filter & search",
      description:
        "Narrow by sector, country, stage, minimum readiness score, or free-text search across company names and value props.",
      side: "bottom",
      align: "start",
    },
  },
  {
    element: '[data-testid="deal-table"]',
    popover: {
      title: "Your deal feed",
      description:
        "Sorted by compatibility. Click a row to open the SME profile and (after the founder approves your interest) the secure data room.",
      side: "top",
      align: "start",
    },
  },
  {
    popover: {
      title: "Next steps",
      description:
        "Refine your <b>Mandate</b> any time to tighten matches. Save deals to your <b>Pipeline</b> with stages (Viewed → Saved → VDR opened → Replied → In discussion → Closed). " +
        "Toggle between <b>Businesses and Projects</b> in the Terminal to match your deal focus.",
      side: "over",
      align: "center",
    },
  },
];

function filterAvailableFunderSteps() {
  return FUNDER_STEPS.filter((s) => {
    if (!s.element) return true;
    return document.querySelector(s.element);
  });
}

export function startFunderTour() {
  const steps = filterAvailableFunderSteps();
  if (steps.length === 0) return;
  const tour = driver({
    ...driverConfig,
    steps,
    onDestroyed: () => {
      safeLocalStorageSet(FUNDER_STORAGE_KEY, new Date().toISOString());
    },
  });
  tour.drive();
}

export function maybeStartFunderTourOnFirstVisit() {
  if (safeLocalStorageGet(FUNDER_STORAGE_KEY)) return false;
  setTimeout(startFunderTour, 700);
  return true;
}

export function resetFunderTour() {
  safeLocalStorageRemove(FUNDER_STORAGE_KEY);
}

/* ============================================================
 *          PROJECT founder tours (Intake + Report)
 * ============================================================ */
const PROJECT_INTAKE_KEY = "clinq-project-intake-tour-completed-v1";
const PROJECT_REPORT_KEY = "clinq-project-report-tour-completed-v1";

const PROJECT_INTAKE_STEPS = [
  {
    popover: {
      title: "Welcome to your Project workspace 👋",
      description:
        "A 60-second tour of the Project Intake — where your Project Readiness Score is built. " +
        "Replay it any time from the Tutorial page.",
      side: "over",
      align: "center",
    },
  },
  {
    element: '[data-testid="intake-completeness"]',
    popover: {
      title: "Your completeness meter",
      description:
        "Tracks how much of the intake you've answered. The more you complete, the firmer and higher your score can go.",
      side: "bottom",
      align: "start",
    },
  },
  {
    element: '[data-testid="intake-stepper"]',
    popover: {
      title: "Guided sections",
      description:
        "Sponsor track record, land & permits, technical readiness, revenue security, capital stack and risk. Jump to any section — answers save as you go.",
      side: "bottom",
      align: "start",
    },
  },
  {
    element: '[data-testid="intake-next-btn"]',
    popover: {
      title: "Save & continue",
      description:
        "Each Next saves your answers. You can leave and come back — nothing is lost.",
      side: "top",
      align: "end",
    },
  },
  {
    element: '[data-testid="run-prs-analysis-btn"]',
    popover: {
      title: "Run the analysis",
      description:
        "On the final Documents section, upload your evidence (feasibility, land title, permits…) and click Analyse my Documents. Your first run is free.",
      side: "top",
      align: "start",
    },
  },
  {
    element: '[data-testid="intake-view-report-btn"]',
    popover: {
      title: "Your Readiness Report",
      description:
        "Once analysed, your Project Readiness Score and full report live here.",
      side: "bottom",
      align: "end",
    },
  },
  {
    popover: {
      title: "That's the intake",
      description:
        "Your PRS is scored 0–100 across <b>six pillars</b>, weighted for the funder type you're targeting (bank, DFI, equity or grant). " +
        "Hard checks — like an unresolved land title — cap the score until fixed.",
      side: "over",
      align: "center",
    },
  },
];

const PROJECT_REPORT_STEPS = [
  {
    popover: {
      title: "Your Project Readiness Report 👋",
      description:
        "A quick tour of how to read — and improve — your score. Replay any time from the Tutorial page.",
      side: "over",
      align: "center",
    },
  },
  {
    element: '[data-testid="prs-score-hero"]',
    popover: {
      title: "Your PRS",
      description:
        "0–100, weighted for your target funder type. The band chip tells you how funders will read it at a glance.",
      side: "bottom",
      align: "start",
    },
  },
  {
    element: '[data-testid="prs-pillar-bars"]',
    popover: {
      title: "The six pillars",
      description:
        "Sponsor, land & permits, technical, revenue security, capital stack and risk. Weak pillars show you exactly where to focus.",
      side: "left",
      align: "start",
    },
  },
  {
    element: '[data-testid="prs-unlock-cta"]',
    popover: {
      title: "Unlock the full report",
      description:
        "Strengths & gaps with evidence, all ten hard checks, capital-structure verdicts, and a 90-day improvement roadmap.",
      side: "top",
      align: "center",
    },
  },
  {
    element: '[data-testid="prs-verdicts"]',
    popover: {
      title: "Capital-structure verdicts",
      description:
        "How bank debt, DFI, equity and grant funders would each read your project today.",
      side: "top",
      align: "start",
    },
  },
  {
    element: '[data-testid="prs-roadmap"]',
    popover: {
      title: "Your 90-day roadmap",
      description:
        "Prioritised actions to lift your score. Fix items, update the intake, then re-run the analysis.",
      side: "top",
      align: "start",
    },
  },
  {
    element: '[data-testid="prs-rerun-btn"]',
    popover: {
      title: "Re-run after improving",
      description:
        "Each re-run uses 1 credit and re-scores the project against your updated intake and documents.",
      side: "bottom",
      align: "end",
    },
  },
  {
    popover: {
      title: "Aim for Verified",
      description:
        "Projects scoring <b>55+</b> can apply for a hand-reviewed <b>Verified badge</b> — the strongest signal to investors browsing the funder terminal.",
      side: "over",
      align: "center",
    },
  },
];

function makeTour(steps, storageKey) {
  const available = steps.filter((s) => !s.element || document.querySelector(s.element));
  if (available.length === 0) return null;
  return driver({
    ...driverConfig,
    steps: available,
    onDestroyed: () => safeLocalStorageSet(storageKey, new Date().toISOString()),
  });
}

export function startProjectIntakeTour() {
  const tour = makeTour(PROJECT_INTAKE_STEPS, PROJECT_INTAKE_KEY);
  if (tour) tour.drive();
}

export function maybeStartProjectIntakeTourOnFirstVisit() {
  if (safeLocalStorageGet(PROJECT_INTAKE_KEY)) return false;
  setTimeout(startProjectIntakeTour, 700);
  return true;
}

export function resetProjectIntakeTour() {
  safeLocalStorageRemove(PROJECT_INTAKE_KEY);
}

export function startProjectReportTour() {
  const tour = makeTour(PROJECT_REPORT_STEPS, PROJECT_REPORT_KEY);
  if (tour) tour.drive();
}

export function maybeStartProjectReportTourOnFirstVisit() {
  if (safeLocalStorageGet(PROJECT_REPORT_KEY)) return false;
  setTimeout(startProjectReportTour, 700);
  return true;
}

export function resetProjectReportTour() {
  safeLocalStorageRemove(PROJECT_REPORT_KEY);
}
